import type { Locale } from "@commerce-frontend/i18n/types";
import { graphql } from "@commerce-frontend/types";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import { Link } from "@commerce-frontend/ui/primitives/Link";
import type { ResultOf } from "@graphql-typed-document-node/core";
import { useTranslations } from "next-intl";
import { BreadcrumbsJsonLd } from "~/lib/jsonLd";

const breadcrumbFragment = graphql(/* GraphQL */ `
	fragment BreadcrumbFragment on Breadcrumb {
		name
		path
	}
`);

type Props = {
	breadcrumbs: ResultOf<typeof breadcrumbFragment>[];
	locale: Locale;
	className?: string;
};

export const Breadcrumbs = ({ className, locale, breadcrumbs }: Props) => {
	const t = useTranslations("Breadcrumbs");
	const filteredBreadcrumbs = breadcrumbs.filter((item) => item.name !== "Homepage");
	const previousPage = filteredBreadcrumbs.at(-2)?.path;

	return (
		<nav aria-label="Breadcrumbs" className={cn("", className)}>
			<BreadcrumbsJsonLd breadcrumbs={breadcrumbs} />

			<ol className="flex max-w-full flex-row items-center gap-md py-3xl">
				<li>
					<Link
						href={{ pathname: "/" }}
						className="hidden self-center lg:flex"
						aria-label="homepage"
					>
						<Icon icon="home-line" className="size-5 bg-brand" />
					</Link>
				</li>

				<li>
					<Link
						href={previousPage || `/${locale.toLowerCase()}/`}
						className="flex self-center lg:hidden"
					>
						<Icon icon="chevron-left" className="size-5 bg-brand" />
						<span className="sr-only">{t("go-back")}</span>
					</Link>
				</li>

				{filteredBreadcrumbs.map((item, index) => (
					<li
						key={index}
						className={`${index < filteredBreadcrumbs.length - 1 ? "hidden lg:flex" : "flex shrink grow"} max-w-full gap-md text-sm lg:before:size-4 lg:before:self-center lg:before:bg-gray-300 lg:before:content-[''] lg:before:[mask-image:--icon-chevron-right] lg:before:[mask-position:center] lg:before:[mask-repeat:no-repeat] lg:before:[mask-size:contain]`}
					>
						{index < filteredBreadcrumbs.length - 1 ? (
							<Link
								className="inline-block self-center text-nowrap font-medium text-brand underline underline-offset-2"
								href={item.path}
							>
								{item.name}
							</Link>
						) : (
							<span
								className="inline-block max-w-[calc(100%-28px)] truncate rounded-sm bg-orange-dark-200 px-md py-xs font-semibold lg:max-w-xl"
								aria-current="page"
							>
								{item.name}
							</span>
						)}
					</li>
				))}
			</ol>
		</nav>
	);
};
